import { loadSave } from './save';

export type SfxName =
  | 'pop'
  | 'swap'
  | 'swap-fail'
  | 'hit'
  | 'power-ready'
  | 'power-fire'
  | 'victory'
  | 'defeat';

const bgmUrls = import.meta.glob('../assets/audio/bgm.{mp3,ogg,wav}', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

let ctx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let bgmAudio: HTMLAudioElement | null = null;
let lastPopAtMs = 0;

export function initAudio() {
  if (ctx) {
    if (ctx.state === 'suspended') void ctx.resume();
    return;
  }
  const Ctor = (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
  if (!Ctor) return;
  ctx = new Ctor();
  masterGain = ctx.createGain();
  masterGain.gain.value = loadSave().audioMuted ? 0 : 1;
  masterGain.connect(ctx.destination);
}

export function applyMuteFromSave() {
  if (!masterGain || !ctx) return;
  const muted = loadSave().audioMuted;
  masterGain.gain.setTargetAtTime(muted ? 0 : 1, ctx.currentTime, 0.05);
  if (bgmAudio) {
    bgmAudio.muted = muted;
    if (muted) bgmAudio.pause();
    else void bgmAudio.play().catch(() => undefined);
  }
}

export function startBGM() {
  const entries = Object.values(bgmUrls);
  if (entries.length === 0) return;
  if (bgmAudio) return;
  const url = entries[0];
  bgmAudio = new Audio(url);
  bgmAudio.loop = true;
  bgmAudio.volume = 0.25;
  bgmAudio.muted = loadSave().audioMuted;
  void bgmAudio.play().catch(() => undefined);
}

export function stopBGM() {
  if (!bgmAudio) return;
  bgmAudio.pause();
  bgmAudio.currentTime = 0;
  bgmAudio = null;
}

export function playSFX(name: SfxName) {
  if (!ctx || !masterGain) return;
  if (loadSave().audioMuted) return;
  if (name === 'pop') {
    const now = performance.now();
    if (now - lastPopAtMs < 55) return;
    lastPopAtMs = now;
  }
  const t = ctx.currentTime;
  switch (name) {
    case 'pop':
      playTone(t, { freq: 440, endFreq: 880, durMs: 90, type: 'sine', peak: 0.22 });
      return;
    case 'swap':
      playTone(t, { freq: 320, endFreq: 540, durMs: 130, type: 'sine', peak: 0.25 });
      return;
    case 'swap-fail':
      playTone(t, { freq: 220, endFreq: 110, durMs: 170, type: 'square', peak: 0.16 });
      return;
    case 'hit':
      playNoiseBurst(t, { durMs: 220, peak: 0.35, lowpass: 600 });
      playTone(t + 0.01, { freq: 180, endFreq: 70, durMs: 140, type: 'sine', peak: 0.24 });
      return;
    case 'power-ready':
      playArpeggio(t, { freqs: [523.25, 659.25, 783.99], stepMs: 90, durMs: 140, type: 'triangle', peak: 0.22 });
      return;
    case 'power-fire':
      playNoiseBurst(t, { durMs: 280, peak: 0.3, lowpass: 1800 });
      playTone(t + 0.02, { freq: 660, endFreq: 220, durMs: 320, type: 'sawtooth', peak: 0.28 });
      return;
    case 'victory':
      playArpeggio(t, { freqs: [523.25, 659.25, 783.99, 1046.5], stepMs: 120, durMs: 200, type: 'triangle', peak: 0.28 });
      return;
    case 'defeat':
      playArpeggio(t, { freqs: [392.0, 329.63, 261.63], stepMs: 150, durMs: 260, type: 'sine', peak: 0.25 });
      return;
  }
}

type ToneOpts = {
  freq: number;
  endFreq?: number;
  durMs: number;
  type: OscillatorType;
  peak: number;
};

function playTone(t0: number, opts: ToneOpts) {
  if (!ctx || !masterGain) return;
  const osc = ctx.createOscillator();
  osc.type = opts.type;
  osc.frequency.setValueAtTime(opts.freq, t0);
  if (opts.endFreq != null) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, opts.endFreq), t0 + opts.durMs / 1000);
  }
  const g = ctx.createGain();
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(opts.peak, t0 + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + opts.durMs / 1000);
  osc.connect(g);
  g.connect(masterGain);
  osc.start(t0);
  osc.stop(t0 + opts.durMs / 1000 + 0.02);
}

type NoiseOpts = {
  durMs: number;
  peak: number;
  lowpass?: number;
};

function playNoiseBurst(t0: number, opts: NoiseOpts) {
  if (!ctx || !masterGain) return;
  const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * (opts.durMs / 1000)), ctx.sampleRate);
  const ch = buf.getChannelData(0);
  for (let i = 0; i < ch.length; i++) ch[i] = (Math.random() * 2 - 1) * (1 - i / ch.length);
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const g = ctx.createGain();
  g.gain.setValueAtTime(opts.peak, t0);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + opts.durMs / 1000);
  let last: AudioNode = src;
  if (opts.lowpass) {
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = opts.lowpass;
    src.connect(lp);
    last = lp;
  }
  last.connect(g);
  g.connect(masterGain);
  src.start(t0);
  src.stop(t0 + opts.durMs / 1000 + 0.02);
}

type ArpeggioOpts = {
  freqs: number[];
  stepMs: number;
  durMs: number;
  type: OscillatorType;
  peak: number;
};

function playArpeggio(t0: number, opts: ArpeggioOpts) {
  opts.freqs.forEach((freq, i) => {
    playTone(t0 + (i * opts.stepMs) / 1000, {
      freq,
      durMs: opts.durMs,
      type: opts.type,
      peak: opts.peak,
    });
  });
}
